import lodash from 'lodash'
import React, { useCallback, useEffect, useMemo } from 'react'
import { ReactElement } from 'react'

import Currency from '../../../../../../../1stlog-ui/src/components/Currency'
import CurrencyInput from '../../../../../../../1stlog-ui/src/components/CurrencyInput'
import {
  Label,
  useField,
} from '../../../../../../../1stlog-ui/src/components/Field'
import {
  composeValidators,
  mustBePositive,
  required,
} from '../../../../../../../1stlog-ui/src/components/Field/Validation'
import InlineEditorInvalid from '../../../../../../../1stlog-ui/src/components/InlineEditor/InlineEditorInvalid'
import { usePrevious } from '../../../../../../../1stlog-ui/src/hooks'
import { useProductSettings } from '../../../../Products'
import useOrderEntry from '../../../../useOrderEntry'

type TValueOfGoodsProps = {
  readonly?: boolean
}

const ValueOfGoods = (props: TValueOfGoodsProps): ReactElement => {
  const { readonly } = props

  const { currentCustomer } = useOrderEntry()

  const { isvalueOfGoodsAllowed, isValueOfGoodsRequired, maxValueOfGoods } =
    useProductSettings()

  const shipmentServices = useField('shipmentServices')

  const [valueOfGoods, setValueOfGoods] = useField('valueOfGoods', {
    asState: true,
  })

  const isProformaLightEnabled = useMemo(
    () => lodash.some(shipmentServices, (s) => s.name === 'PFL' && s.isEnabled),
    [shipmentServices],
  )

  const isInsuranceEnabled = useMemo(
    () => lodash.some(shipmentServices, (s) => s.name === 'INS' && s.isEnabled),
    [shipmentServices],
  )

  const isEnabled = useMemo(
    () => isvalueOfGoodsAllowed || isProformaLightEnabled || isInsuranceEnabled,
    [isvalueOfGoodsAllowed, isProformaLightEnabled, isInsuranceEnabled],
  )

  const isRequired = useMemo(
    () =>
      isProformaLightEnabled || isInsuranceEnabled || isValueOfGoodsRequired,
    [isProformaLightEnabled, isInsuranceEnabled, isValueOfGoodsRequired],
  )

  const previousIsEnabled = usePrevious(isEnabled)
  useEffect(() => {
    // set defaults
    if (!lodash.get(valueOfGoods, 'currency')) {
      setValueOfGoods({
        currency: lodash.get(currentCustomer, 'defaultCurrency', 'EUR'),
        value: 0,
      })
    }
    // reset
    if (previousIsEnabled && !isEnabled) {
      setValueOfGoods(undefined)
    }
  }, [isEnabled])

  const validateMaxValue = useCallback(
    (value) => {
      if (maxValueOfGoods && value > maxValueOfGoods) {
        return i18n.t(
          'maximum_value_of_goods_exceeded_warning',
          'Die Sendung kann nicht angemeldet werden. Bitte wenden Sie sich an Ihren Versandpartner und fragen Sie nach Spezialoptionen.',
        )
      }
    },
    [maxValueOfGoods],
  )

  if (!isEnabled) {
    return <></>
  }

  return (
    <>
      {readonly && (
        <div className="text-center">
          <div>
            <Label>{i18n.t('value_of_goods', 'Warenwert')}</Label>
          </div>
          <h4 className="mt-1">
            {isNaN(valueOfGoods?.value) ? (
              <InlineEditorInvalid />
            ) : (
              <Currency
                value={valueOfGoods?.value}
                currency={valueOfGoods?.currency}
              />
            )}
          </h4>
        </div>
      )}
      <div hidden={readonly}>
        <CurrencyInput
          key={String(isRequired)}
          name="valueOfGoods"
          label={i18n.t('value_of_goods', 'Warenwert')}
          allowZeroValue
          required={isRequired}
          validate={composeValidators(
            validateMaxValue,
            isRequired ? required : undefined,
            isRequired ? mustBePositive : undefined,
          )}
        />
      </div>
    </>
  )
}

export default ValueOfGoods
